import type {
  AnswerSpec,
  WorkflowDefinitionGraph,
  WorkflowLoop,
  WorkflowStep,
} from '../api/workflows';
import type { ConsentType } from '../api/consent';

export type AuthorableKind =
  | 'collect_questions'
  | 'collect_images'
  | 'decision'
  | 'output'
  | 'run_ai_review'
  | 'gate_on_consent'
  | 'halt'
  | 'set_case_status'
  | 'loop_control';

/**
 * Per-kind placeholder params used when the operator drops a new node.
 * Real authoring lands in PR 3.3's per-node side panel — the
 * placeholders here keep the definition JSON parseable by the
 * client-side schema while leaving "must-fill" fields obviously
 * empty (so the orchestrator's `validate()` will flag them on
 * publish, but Save Draft is permissive).
 */
export function defaultParamsForKind(kind: AuthorableKind): Record<string, unknown> {
  switch (kind) {
    case 'collect_questions':
      return { questions: [] };
    case 'collect_images':
      return { configurations: [] };
    case 'decision':
      return { input: { step_id: '', question_id: '' }, branches: [] };
    case 'output':
      return { fields: [{ name: 'result', type: 'string' }] };
    case 'run_ai_review':
      // Server-driven kind — runtime supplies the inputs. No per-step
      // params authored from the editor yet.
      return {};
    case 'gate_on_consent':
      // Config-driven gate — no authored params; the runtime resolves the
      // blocking consents for the case's product/organisation.
      return {};
    case 'halt':
      return { reason_code: '' };
    case 'set_case_status':
      return { status: '' };
    case 'loop_control':
      // No authored params — repeat/done live in transitions, and
      // membership/start/max_iterations live on the owning `loops`
      // entry (created alongside this step; see addLoop).
      return {};
  }
}

/**
 * Pick the next available step id for a new node of this kind. Format
 * is `<kind>_<n>` where n is the smallest positive integer that
 * doesn't collide with an existing step id. The autoincrement is
 * per-kind so the operator gets `collect_questions_1`,
 * `collect_questions_2`, … rather than a global counter.
 */
export function nextStepId(graph: WorkflowDefinitionGraph, kind: AuthorableKind): string {
  let n = 1;
  while (graph.steps[`${kind}_${n}`]) n += 1;
  return `${kind}_${n}`;
}

/**
 * Pick the next available outcome name for a new edge from a source
 * step. Non-decision steps converge on `on_complete` (auto-incremented
 * if the source already has one); decision steps get `branch_<n>` so
 * the operator can hook them up to params later. PR 3.3's side panel
 * lets operators rename outcomes once per-node config is wired.
 */
export function nextOutcomeName(graph: WorkflowDefinitionGraph, sourceId: string): string {
  const source = graph.steps[sourceId];
  const existing = new Set(Object.keys(source?.transitions ?? {}));
  const base = source?.kind === 'decision' ? 'branch' : 'on_complete';
  if (!existing.has(base)) return base;
  let n = 2;
  while (existing.has(`${base}_${n}`)) n += 1;
  return `${base}_${n}`;
}

/**
 * Stringify-equality check on definitions. Used to drive the editor's
 * "unsaved" indicator and the warn-on-leave guard. Cheap (the
 * definitions are bounded in size) and safe — both sides are pure
 * JSON so reference identity isn't useful.
 */
export function definitionsEqual(a: unknown, b: unknown): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}

/**
 * Pick the next available loop id. Format is `loop_<n>` — a separate
 * namespace from step ids since loop keys and step ids never collide
 * by construction (loops is a sibling map, not part of `steps`).
 */
export function nextLoopId(graph: WorkflowDefinitionGraph): string {
  let n = 1;
  while (graph.loops?.[`loop_${n}`]) n += 1;
  return `loop_${n}`;
}

/**
 * True if `stepId` is a member of any loop. Used to tint loop-member
 * nodes on the canvas and to keep the membership picker from offering
 * a step that already belongs to a different loop (v1: no shared
 * members, mirrors the backend validator).
 */
export function isLoopMember(graph: WorkflowDefinitionGraph, stepId: string): boolean {
  return Object.values(graph.loops ?? {}).some((loop) => loop.steps.includes(stepId));
}

/**
 * Find the `[loopId, loop]` pair whose `control` step is `controlStepId`.
 * Every `loop_control` node owns exactly one loop (created alongside it
 * on drop), so this is how the side panel resolves "which loop am I
 * configuring" from the selected step id alone.
 */
export function findLoopByControl(
  graph: WorkflowDefinitionGraph,
  controlStepId: string,
): [string, WorkflowLoop] | undefined {
  return Object.entries(graph.loops ?? {}).find(([, loop]) => loop.control === controlStepId);
}

/**
 * Warn when an `output` step field's `source` references a loop member
 * step directly (bare `"<stepId>"` or dotted `"<stepId>.<question_id>"`).
 * Inside a loop, `ctx[stepId]` is overwritten every pass — only the
 * whole-array `ctx[loopId]` (referenced by a bare `source` equal to the
 * loop id itself) captures every iteration. See `assembleOutput` in
 * `services/orchestrator/src/instances/client-driven.ts` for the
 * resolution this mirrors. Returns the owning loop id to suggest instead,
 * or `null` if `source` doesn't reference a loop member (including when
 * it already correctly references the loop id itself).
 */
export function outputSourceLoopWarning(
  graph: WorkflowDefinitionGraph,
  source: string | undefined,
): string | null {
  if (!source || !graph.loops) return null;
  const dot = source.indexOf('.');
  const stepId = dot === -1 ? source : source.slice(0, dot);
  const found = Object.entries(graph.loops).find(([, loop]) => loop.steps.includes(stepId));
  return found ? found[0] : null;
}

/**
 * Create a new loop owned by `controlStepId`, no other members and no
 * start yet. The control step is always included in `loop.steps` — the
 * backend validator requires `control ∈ steps` — so callers editing
 * membership (see `LoopControlForm`) only ever add/remove the *other*
 * members and must re-append `controlStepId` themselves. Called when a
 * `loop_control` node is dropped onto the canvas — every other
 * authorable kind is a single palette drop, so a loop_control drop must
 * fully bootstrap its owning loop in the same step rather than
 * requiring a separate creation flow.
 */
export function addLoop(
  graph: WorkflowDefinitionGraph,
  controlStepId: string,
): WorkflowDefinitionGraph {
  const loopId = nextLoopId(graph);
  return {
    ...graph,
    loops: {
      ...(graph.loops ?? {}),
      [loopId]: { control: controlStepId, steps: [controlStepId], start: '' },
    },
  };
}

/**
 * Replace a loop's membership set. If the current `start` step drops
 * out of the new membership, both `loop.start` and the control step's
 * `repeat` transition are cleared — `repeat` must always equal
 * `loop.start` exactly (a validator invariant), so it can never be left
 * pointing at a step that's no longer a member. Symmetrically, if the
 * control's current `done` target is now included in the new
 * membership, `done` is cleared too — `done` must always exit the loop,
 * so it can never be left pointing at a step that just became a member.
 * No-op for an unknown loop id.
 */
export function setLoopMembership(
  graph: WorkflowDefinitionGraph,
  loopId: string,
  memberIds: string[],
): WorkflowDefinitionGraph {
  const loop = graph.loops?.[loopId];
  if (!loop) return graph;
  const startStillMember = memberIds.includes(loop.start);
  let nextGraph: WorkflowDefinitionGraph = {
    ...graph,
    loops: {
      ...graph.loops,
      [loopId]: { ...loop, steps: memberIds, start: startStillMember ? loop.start : '' },
    },
  };
  if (!startStillMember) {
    nextGraph = setTransition(nextGraph, loop.control, 'repeat', '');
  }
  const doneTarget = graph.steps[loop.control]?.transitions?.done;
  if (doneTarget && memberIds.includes(doneTarget)) {
    nextGraph = setTransition(nextGraph, loop.control, 'done', '');
  }
  return nextGraph;
}

/**
 * Set a loop's start step and sync the control step's `repeat`
 * transition to match. `repeat` is never independently editable in the
 * UI (side panel decision 3) — this is the only path that writes it.
 * No-op for an unknown loop id.
 */
export function setLoopStart(
  graph: WorkflowDefinitionGraph,
  loopId: string,
  stepId: string,
): WorkflowDefinitionGraph {
  const loop = graph.loops?.[loopId];
  if (!loop) return graph;
  const nextGraph: WorkflowDefinitionGraph = {
    ...graph,
    loops: { ...graph.loops, [loopId]: { ...loop, start: stepId } },
  };
  return setTransition(nextGraph, loop.control, 'repeat', stepId);
}

/**
 * Set or clear a loop's `max_iterations`. `undefined` clears it (the
 * runtime falls back to its default of 3). No-op for an unknown loop
 * id.
 */
export function setLoopMaxIterations(
  graph: WorkflowDefinitionGraph,
  loopId: string,
  max: number | undefined,
): WorkflowDefinitionGraph {
  const loop = graph.loops?.[loopId];
  if (!loop) return graph;
  const nextLoop = { ...loop };
  if (max === undefined) {
    delete nextLoop.max_iterations;
  } else {
    nextLoop.max_iterations = max;
  }
  return { ...graph, loops: { ...graph.loops, [loopId]: nextLoop } };
}

/**
 * Toggle whether a loop is scoped to a lesion (`per_finding`). When on,
 * each repeat/done advance must carry the `finding_id` of the lesion
 * that pass covered. No-op for an unknown loop id.
 */
export function setLoopPerFinding(
  graph: WorkflowDefinitionGraph,
  loopId: string,
  perFinding: boolean,
): WorkflowDefinitionGraph {
  const loop = graph.loops?.[loopId];
  if (!loop) return graph;
  const nextLoop = { ...loop };
  if (perFinding) {
    nextLoop.per_finding = true;
  } else {
    delete nextLoop.per_finding;
  }
  return { ...graph, loops: { ...graph.loops, [loopId]: nextLoop } };
}

/**
 * Remove a set of step ids from the graph and prune any transitions
 * that pointed at them. If the start step was removed, fall back to
 * the first remaining step (the operator sees a temporarily
 * inconsistent graph until they pick a new start, but Save Draft is
 * permissive — Publish (PR 3.4) refuses on validation failure).
 */
export function removeSteps(
  graph: WorkflowDefinitionGraph,
  removedIds: Set<string>,
): WorkflowDefinitionGraph {
  const nextSteps: WorkflowDefinitionGraph['steps'] = {};
  for (const [id, step] of Object.entries(graph.steps)) {
    if (removedIds.has(id)) continue;
    const trans = Object.fromEntries(
      Object.entries(step.transitions ?? {}).filter(([, target]) => !removedIds.has(target)),
    );
    nextSteps[id] = {
      ...step,
      transitions: Object.keys(trans).length ? trans : undefined,
    } as WorkflowStep;
  }
  const startStep = removedIds.has(graph.start_step)
    ? (Object.keys(nextSteps)[0] ?? graph.start_step)
    : graph.start_step;

  // Drop a loop entirely if its control step was removed (the loop has
  // no meaning without it); otherwise prune removed members and clear
  // an orphaned start, same as a transition target pointing at a
  // removed step gets pruned above.
  let nextLoops: WorkflowDefinitionGraph['loops'];
  if (graph.loops) {
    const kept = Object.entries(graph.loops)
      .filter(([, loop]) => !removedIds.has(loop.control))
      .map(([loopId, loop]) => {
        const steps = loop.steps.filter((s) => !removedIds.has(s));
        const start = removedIds.has(loop.start) ? '' : loop.start;
        return [loopId, { ...loop, steps, start }] as const;
      });
    nextLoops = kept.length ? Object.fromEntries(kept) : undefined;
  }

  return { ...graph, start_step: startStep, steps: nextSteps, loops: nextLoops };
}

/**
 * Rename a step. Updates the steps map key, any transitions pointing
 * at the old id, the start_step pointer if it matched, and any
 * `decision` step's `params.input.step_id` references. No-op if the
 * old id doesn't exist or the new id is the same. Returns the
 * original graph unchanged if the new id collides with an existing
 * step (caller should validate before calling).
 */
export function renameStep(
  graph: WorkflowDefinitionGraph,
  oldId: string,
  newId: string,
): WorkflowDefinitionGraph {
  if (oldId === newId) return graph;
  if (!graph.steps[oldId]) return graph;
  if (graph.steps[newId]) return graph; // collision — caller must check first

  const nextSteps: WorkflowDefinitionGraph['steps'] = {};
  for (const [id, step] of Object.entries(graph.steps)) {
    const newKey = id === oldId ? newId : id;
    // Update transition targets that pointed at the renamed step.
    const trans = step.transitions
      ? Object.fromEntries(
          Object.entries(step.transitions).map(([outcome, target]) => [
            outcome,
            target === oldId ? newId : target,
          ]),
        )
      : undefined;
    // Update decision input.step_id if it referenced the renamed step.
    let nextStep = { ...step, transitions: trans } as WorkflowStep;
    if (step.kind === 'decision') {
      const params = step.params as
        | { input?: { step_id?: string; question_id?: string } }
        | undefined;
      if (params?.input?.step_id === oldId) {
        nextStep = {
          ...nextStep,
          params: {
            ...(step.params as Record<string, unknown>),
            input: { ...params.input, step_id: newId },
          },
        } as WorkflowStep;
      }
    }
    nextSteps[newKey] = nextStep;
  }

  // Remap the loops block so a rename never leaves `control`, `start`,
  // or a membership entry pointing at the now-dead old id (mirrors the
  // pruning `removeSteps` does above for deletions).
  const nextLoops = graph.loops
    ? Object.fromEntries(
        Object.entries(graph.loops).map(([loopId, loop]) => [
          loopId,
          {
            ...loop,
            control: loop.control === oldId ? newId : loop.control,
            start: loop.start === oldId ? newId : loop.start,
            steps: loop.steps.map((s) => (s === oldId ? newId : s)),
          },
        ]),
      )
    : graph.loops;

  return {
    ...graph,
    start_step: graph.start_step === oldId ? newId : graph.start_step,
    steps: nextSteps,
    loops: nextLoops,
  };
}

/**
 * Replace a single step's content (kind stays put unless the caller
 * supplies a new step with a different kind). The most common use
 * case is the side panel updating per-kind params after an edit.
 */
export function updateStep(
  graph: WorkflowDefinitionGraph,
  id: string,
  next: WorkflowStep,
): WorkflowDefinitionGraph {
  if (!graph.steps[id]) return graph;
  return { ...graph, steps: { ...graph.steps, [id]: next } };
}

/**
 * Set (or clear) a single named transition outcome on a step, returning
 * a new graph. An empty `target` removes the outcome; if that leaves the
 * step with no transitions, `transitions` becomes `undefined`. No-op (same
 * reference) when the step id doesn't exist. Used by the branching
 * `gate_on_consent` pickers (on_pass / on_fail) in the side panel.
 */
export function setTransition(
  graph: WorkflowDefinitionGraph,
  stepId: string,
  outcome: string,
  target: string,
): WorkflowDefinitionGraph {
  const step = graph.steps[stepId];
  if (!step) return graph;
  const rest = { ...(step.transitions ?? {}) };
  if (target) {
    rest[outcome] = target;
  } else {
    delete rest[outcome];
  }
  const nextStep = {
    ...step,
    transitions: Object.keys(rest).length ? rest : undefined,
  } as WorkflowStep;
  return { ...graph, steps: { ...graph.steps, [stepId]: nextStep } };
}

/**
 * Selectable consents for a consent gate in a workflow scoped to a
 * product (and optionally an org). Product-scoped workflows
 * (workflowOrgId null) see only product-default consents (org-agnostic
 * codes); org-scoped workflows additionally see that org's additions.
 * Inactive consents are excluded. Returns `{ code, display_name }` —
 * `code` is what goes into `params.types`.
 */
export function consentGateOptions(
  consents: ConsentType[],
  workflowOrgId: string | null,
): Array<{ code: string; display_name: string }> {
  return consents
    .filter(
      (c) =>
        c.active &&
        (c.organisation_id === null ||
          (workflowOrgId !== null && c.organisation_id === workflowOrgId)),
    )
    .map((c) => ({ code: c.code, display_name: c.display_name }));
}

/**
 * Set a consent gate's `params.types` to a single consent code (or clear
 * it to `[]` when `code` is empty — the runtime then falls back to all
 * blocking consents). Immutable; no-op (same reference) for an unknown
 * step id. Preserves the step's other params/fields.
 */
export function setConsentType(
  graph: WorkflowDefinitionGraph,
  stepId: string,
  code: string,
): WorkflowDefinitionGraph {
  const step = graph.steps[stepId];
  if (!step) return graph;
  const types = code ? [code] : [];
  const next = { ...step, params: { ...(step.params ?? {}), types } } as WorkflowStep;
  return { ...graph, steps: { ...graph.steps, [stepId]: next } };
}

/**
 * Set (or clear) a `run_ai_review` step's `params.await_completion`. `true`
 * (the runtime default when the key is absent) deletes the key so the
 * definition JSON stays minimal for the common blocking case; `false` sets
 * it explicitly to opt into the non-blocking dispatch mode (#1292). No-op
 * (same reference) for an unknown step id.
 */
export function setAwaitCompletion(
  graph: WorkflowDefinitionGraph,
  stepId: string,
  awaitCompletion: boolean,
): WorkflowDefinitionGraph {
  const step = graph.steps[stepId];
  if (!step) return graph;
  const params: Record<string, unknown> = { ...(step.params ?? {}) };
  if (awaitCompletion) {
    delete params.await_completion;
  } else {
    params.await_completion = false;
  }
  const next = { ...step, params } as WorkflowStep;
  return { ...graph, steps: { ...graph.steps, [stepId]: next } };
}

/**
 * Return step ids sorted by `order` (ascending). Steps missing an
 * `order` field fall to the end, sorted by their JSON-key ordering
 * (which is the insertion order — what the operator typed).
 */
export function stepsByOrder(graph: WorkflowDefinitionGraph): string[] {
  const entries = Object.entries(graph.steps);
  return entries
    .map(([id, step], idx) => ({ id, order: step.order, idx }))
    .sort((a, b) => {
      const aHas = typeof a.order === 'number';
      const bHas = typeof b.order === 'number';
      if (aHas && bHas) return (a.order as number) - (b.order as number);
      if (aHas) return -1;
      if (bHas) return 1;
      return a.idx - b.idx;
    })
    .map((x) => x.id);
}

/**
 * Renumber all steps to a contiguous 1..N order based on the current
 * `stepsByOrder` sequence. Called after add/remove/swap so the order
 * field never develops gaps.
 */
export function renumberOrder(graph: WorkflowDefinitionGraph): WorkflowDefinitionGraph {
  const ordered = stepsByOrder(graph);
  const nextSteps: WorkflowDefinitionGraph['steps'] = {};
  for (const id of ordered) {
    nextSteps[id] = { ...graph.steps[id], order: ordered.indexOf(id) + 1 } as WorkflowStep;
  }
  // Preserve any steps not in the ordered list (shouldn't happen, but
  // safe — stepsByOrder includes every step).
  for (const [id, step] of Object.entries(graph.steps)) {
    if (!nextSteps[id]) nextSteps[id] = step;
  }
  return { ...graph, steps: nextSteps };
}

/**
 * Swap the order of `id` with its neighbour in the given direction.
 * Returns the original graph unchanged if there's no neighbour
 * (already at the top/bottom of the sequence).
 */
export function moveStepOrder(
  graph: WorkflowDefinitionGraph,
  id: string,
  direction: 'up' | 'down',
): WorkflowDefinitionGraph {
  const ordered = stepsByOrder(graph);
  const idx = ordered.indexOf(id);
  if (idx === -1) return graph;
  const swapIdx = direction === 'up' ? idx - 1 : idx + 1;
  if (swapIdx < 0 || swapIdx >= ordered.length) return graph;
  const reordered = [...ordered];
  [reordered[idx], reordered[swapIdx]] = [reordered[swapIdx], reordered[idx]];
  const nextSteps: WorkflowDefinitionGraph['steps'] = {};
  for (const stepId of reordered) {
    nextSteps[stepId] = {
      ...graph.steps[stepId],
      order: reordered.indexOf(stepId) + 1,
    } as WorkflowStep;
  }
  return { ...graph, steps: nextSteps };
}

/**
 * Returns the (step_id, question_id) pairs reachable from a
 * `collect_questions` step in the graph, suitable for populating a
 * decision step's "input" picker. Each `collect_questions` step
 * references a question set by id; the caller supplies a
 * `questionsByQuestionSetId` lookup table (typically built from
 * `useQuestionSets` in the editor side panel). Sets that aren't in
 * the map contribute no entries — usually because they haven't loaded
 * yet, which is fine: the dropdown just stays empty until the data
 * arrives.
 */
export function questionRefs(
  graph: WorkflowDefinitionGraph,
  // `prompt` can be either a plain string or a LocalizedString.
  // The dropdown only needs a human-readable label so we collapse
  // to the default variant via `promptText()` below. `answer` is the
  // question's answer spec — used by DecisionForm to render a typed
  // `when` control and store a correctly-typed value.
  questionsByQuestionSetId: Map<
    string,
    Array<{ id: string; prompt: string | { default: string }; answer?: AnswerSpec }>
  >,
): Array<{ stepId: string; questionId: string; prompt: string; answer?: AnswerSpec }> {
  const out: Array<{
    stepId: string;
    questionId: string;
    prompt: string;
    answer?: AnswerSpec;
  }> = [];
  for (const [stepId, step] of Object.entries(graph.steps)) {
    if (step.kind !== 'collect_questions') continue;
    const params = step.params as { question_set_id?: string } | undefined;
    const questions = params?.question_set_id
      ? questionsByQuestionSetId.get(params.question_set_id)
      : undefined;
    for (const q of questions ?? []) {
      out.push({ stepId, questionId: q.id, prompt: promptText(q.prompt), answer: q.answer });
    }
  }
  return out;
}

function promptText(p: string | { default: string }): string {
  return typeof p === 'string' ? p : p.default;
}
